import React from "react";
import {
  Create,
  TextInput,
  ImageInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  SelectArrayInput,
  FormDataConsumer,
  NumberInput,
  CheckboxGroupInput,
  AutocompleteInput,
  BooleanInput,
  usePermissions,
  TimeInput,
} from "react-admin";
import { TabbedForm } from "../common/TabbedForm";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import { PRODUCT_TYPES } from "../enums/ProductTypes";
import {
  validateCustomPercentage,
  validatePercentage,
  validatePrice,
  validateProductDescription,
  validateProductName,
  validateRequired,
  validateTimeOrder,
} from "../utils/Validations";
import { ROLE_TYPES } from "../enums/Roles";
import { Box } from "@mui/material";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import ImagePreview from "../common/ImagePreview";
import {
  formatTimeOnCreate,
  parseTimeOnCreate,
} from "../utils/DateTimeUtils";
import {
  DELIVERY_METHODS_OPTIONS,
  DISCOUNT_DAYS_OPTIONS,
  getProductStatusOptionsByRole,
  PRODUCT_TYPES_OPTIONS,
  PURCHASE_SPLIT_OPTIONS,
} from "../utils/ProductUtils";
import { PURCHASE_SPLIT } from "../enums/PurchaseSplitOptions";
import { useWatch } from "react-hook-form";
import ProductStockTab from "./ProductStockTab";
import { transformProduct } from "./productTransform";

const OfferTimeRangeRow = () => {
  const type = useWatch({ name: "type" });
  // Relative source: the SimpleFormIterator's SourceContext already resolves
  // to offerDeliveryAvailability.<i>.times.<j>
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <TimeInput
        source="opening"
        label="Desde"
        validate={
          type === PRODUCT_TYPES.PURCHASE && validateRequired
        }
        parse={parseTimeOnCreate}
        format={formatTimeOnCreate}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
      <TimeInput
        source="closing"
        label="Hasta"
        validate={
          type === PRODUCT_TYPES.PURCHASE && validateRequired
        }
        parse={parseTimeOnCreate}
        format={formatTimeOnCreate}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
    </Box>
  );
};

const ConditionalOffersTab = (props) => {
  const type = useWatch({ name: "type" });
  if (type !== PRODUCT_TYPES.PURCHASE) return null;
  return (
    <TabbedForm.Tab label="Ofertas" {...props}>
      <ArrayInput
        source="offerDeliveryAvailability"
        label="Validez para Retirar"
        validate={
          type === PRODUCT_TYPES.PURCHASE && validateRequired
        }
      >
        <SimpleFormIterator disableReordering>
          <FormDataConsumer>
            {() => {
              return (
                <>
                  <CheckboxGroupInput
                    source={"days"}
                    label=""
                    choices={DISCOUNT_DAYS_OPTIONS}
                  />
                  <ArrayInput
                    source={"times"}
                    label=""
                    validate={validateTimeOrder}
                  >
                    <SimpleFormIterator disableReordering>
                      <OfferTimeRangeRow />
                    </SimpleFormIterator>
                  </ArrayInput>
                </>
              );
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </TabbedForm.Tab>
  );
};

const ProductCreate = () => {
  const { permissions } = usePermissions();

  return (
    <Create
      title="Crear Producto"
      mutationMode="pessimistic"
      redirect="list"
      transform={transformProduct}
    >
      <TabbedForm>
        <TabbedForm.Tab label="Datos básicos">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <Box sx={{ display: "flex" }}>
                <Box sx={{ flex: 0.5, mr: "2em" }}>
                  <TextInput
                    source="name"
                    label="Nombre"
                    validate={validateProductName}
                    fullWidth
                  />
                  <TextInput
                    multiline
                    source="description"
                    label="Descripción"
                    validate={validateProductDescription}
                    fullWidth
                  />
                  <NumberInput
                    source="price"
                    label="Precio"
                    min={1}
                    validate={validatePrice}
                    onWheel={(e) => e.target.blur()}
                    fullWidth
                  />
                  <SelectInput
                    label="Comisión"
                    source="purchaseSplitOption"
                    choices={PURCHASE_SPLIT_OPTIONS}
                    validate={validateRequired}
                    defaultValue={PURCHASE_SPLIT.RESTAURANT_PERCENTAGE}
                    fullWidth
                  />
                  {permissions === ROLE_TYPES.ADMIN &&
                    formData.purchaseSplitOption ===
                      PURCHASE_SPLIT.CUSTOM_PERCENTAGE && (
                      <NumberInput
                        source="customMorfySplitPercentage"
                        label="Comisión personalizada"
                        min={0}
                        max={90}
                        defaultValue={0}
                        format={(v) => v * 100}
                        parse={(v) => parseFloat(v) / 100}
                        validate={validateCustomPercentage}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                      />
                    )}
                  <SelectInput
                    label="Estado"
                    source="status"
                    choices={getProductStatusOptionsByRole(permissions)}
                    validate={validateRequired}
                    fullWidth
                  />
                  {permissions === ROLE_TYPES.ADMIN && (
                    <BooleanInput
                      source="deactivationLocked"
                      label="Bloquear desactivación"
                      helperText="Si está activo, el local no podrá desactivar este producto"
                    />
                  )}
                </Box>
                <Box sx={{ flex: 0.5 }}>
                  <ReferenceArrayInput
                    source="categories"
                    reference="categories"
                    perPage={100}
                  >
                    <SelectArrayInput
                      optionText="name"
                      validate={validateRequired}
                      label="Categorías"
                      helperText="Puedes elegir más de una categoría"
                      fullWidth
                    />
                  </ReferenceArrayInput>
                  <ReferenceInput
                    source="restaurant"
                    reference="restaurants"
                    label="Restaurante"
                    perPage={100}
                    filter={{
                      activeOrOnboarding: true,
                      type: RESTAURANT_TYPES.REGULAR,
                      toteat: false,
                    }}
                    filterToQuery={(searchText) => ({ name: searchText })}
                  >
                    <AutocompleteInput
                      filterToQuery={(searchText) => ({ name: searchText })}
                      label="Restaurante"
                      optionText="name"
                      fullWidth
                      validate={validateRequired}
                    />
                  </ReferenceInput>
                  <SelectInput
                    label="Tipo"
                    source="type"
                    choices={PRODUCT_TYPES_OPTIONS}
                    validate={validateRequired}
                    fullWidth
                  />

                  <SelectArrayInput
                    label="Métodos de Entrega"
                    source="deliveryMethods"
                    choices={DELIVERY_METHODS_OPTIONS}
                    validate={validateRequired}
                    fullWidth
                  />
                </Box>
              </Box>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Descuentos">
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              return (
                <ArrayInput source="discounts" label="">
                  <SimpleFormIterator
                    disableAdd={formData.discounts?.length >= 7}
                    disableReordering
                  >
                    <CheckboxGroupInput
                      source={`days`}
                      label="Días"
                      choices={DISCOUNT_DAYS_OPTIONS}
                    />
                    <ArrayInput source={`discounts`} label="">
                      <SimpleFormIterator disableReordering>
                        <ReferenceInput
                          source="plan"
                          reference="subscription-plans"
                        >
                          <SelectInput
                            optionText="name"
                            validate={validateRequired}
                            label="Plan"
                          />
                        </ReferenceInput>
                        <NumberInput
                          source="discount"
                          label="% Descuento"
                          min={0}
                          max={100}
                          format={(v) => (v ? (v * 100).toFixed() : null)}
                          parse={(v) => parseFloat(v) / 100}
                          validate={validatePercentage}
                          onWheel={(e) => e.target.blur()}
                        />
                      </SimpleFormIterator>
                    </ArrayInput>
                  </SimpleFormIterator>
                </ArrayInput>
              );
            }}
          </FormDataConsumer>
        </TabbedForm.Tab>

        {permissions === ROLE_TYPES.ADMIN && <ConditionalOffersTab />}

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Stock">
            <FormDataConsumer>
              {({ formData }) => <ProductStockTab formData={formData} />}
            </FormDataConsumer>
          </TabbedForm.Tab>
        )}

        <TabbedForm.Tab label="Imágenes">
          <ImageInput
            source="images"
            accept={{
              "image/*": [],
            }}
            label="Imágenes"
            multiple
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImagePreview source="url" />
          </ImageInput>
        </TabbedForm.Tab>
      </TabbedForm>
    </Create>
  );
};

export default ProductCreate;
